import React, { useEffect, useRef, useState } from "react";


const CameraCapture = ({ onCapture }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const timeoutRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState("");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [cameraFacingMode, setCameraFacingMode] = useState("environment"); // 'user' for front, 'environment' for back
    const [captured, setCaptured] = useState(false);
    const audioRef = useRef(null);

    const startCamera = async (facingMode = cameraFacingMode) => {
        setError("");
        try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                if (navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const hasVideoInput = devices.some(device => device.kind === "videoinput");
                    if (!hasVideoInput) {
                        setError("No camera found on your device.");
                        return;
                    }
                }
                // Stop previous stream if exists
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                }
                const constraints = { video: { facingMode } };
                const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                setStream(mediaStream);
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    await videoRef.current.play();
                }
            } else {
                setError("Camera not available on this device/browser.");
            }
        } catch (err) {
            if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
                setError("No camera found on your device.");
            } else if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
                setError("Camera permission is blocked. Please allow access in your browser or device settings.");
            } else {
                setError("Unable to access camera. Please allow permission and try again.");
            }
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        const hasFrames = video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;
        if (!isReady || !hasFrames) {
            setError("Camera not available or no video feed detected.");
            return;
        }
        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, 320, 240);
        const samplePoints = [
            [0, 0],
            [319, 0],
            [0, 239],
            [319, 239],
            [160, 120]
        ];
        const hasNonBlackPixel = samplePoints.some(([x, y]) => {
            const data = context.getImageData(x, y, 1, 1).data;
            return data[0] > 5 || data[1] > 5 || data[2] > 5;
        });
        if (!hasNonBlackPixel) {
            setError("Camera not available or no video feed detected.");
            return;
        }
        canvas.toBlob(blob => {
            if (blob) {
                onCapture(blob);
                setCaptured(true);
                if (audioRef.current) {
                    audioRef.current.currentTime = 0;
                    // Play sound and ignore interruption errors
                    const playPromise = audioRef.current.play();
                    if (playPromise && typeof playPromise.then === 'function') {
                        playPromise.catch(() => {});
                    }
                }
                setTimeout(() => setCaptured(false), 1200);
            }
        }, "image/jpeg");
    };


    useEffect(() => {
        startCamera(cameraFacingMode);
        timeoutRef.current = setTimeout(() => {
            if (!isReady && !error) {
                setError("Camera not available or no video feed detected.");
            }
        }, 3000);
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraFacingMode]);

    const videoElement = (
        <video
            ref={videoRef}
            width={320}
            height={240}
            className="rounded border"
            autoPlay
            muted
            onCanPlay={() => setIsReady(true)}
        />
    );

    const controls = (
        <div className="flex flex-wrap gap-2 items-center justify-center">
            <select
                value={cameraFacingMode}
                onChange={e => setCameraFacingMode(e.target.value)}
                className="px-2 py-1 rounded border"
            >
                <option value="user">Front Camera</option>
                <option value="environment">Back Camera</option>
            </select>
            <button type="button" onClick={() => startCamera(cameraFacingMode)} className="px-3 py-1 rounded bg-blue-600 text-white">Start Camera</button>
            <button type="button" onClick={capturePhoto} className="px-3 py-1 rounded bg-green-600 text-white" disabled={!isReady}>Capture Photo</button>
            <button type="button" onClick={() => setIsFullscreen(true)} className="px-3 py-1 rounded bg-gray-700 text-white">Fullscreen</button>
        </div>
    );

    return (
        <>
            {/* Camera shutter sound, fallback to a short beep if not supported */}
            <audio
                ref={audioRef}
                preload="auto"
                onError={e => {
                    // fallback: play a beep if the audio file fails to load
                    try {
                        const ctx = new (window.AudioContext || window.webkitAudioContext)();
                        const o = ctx.createOscillator();
                        o.type = 'triangle';
                        o.frequency.value = 880;
                        o.connect(ctx.destination);
                        o.start();
                        setTimeout(() => { o.stop(); ctx.close(); }, 120);
                    } catch {
                        // Intentionally ignore errors when playing fallback beep
                    }
                }}
            >
                <source src="https://cdn.pixabay.com/audio/2022/07/26/audio_124bfa1c82.mp3" type="audio/mpeg" />
                <source src="https://cdn.pixabay.com/audio/2022/07/26/audio_124bfa1c82.ogg" type="audio/ogg" />
            </audio>
            {!isFullscreen && (
                <div className="flex flex-col items-center gap-2 relative">
                    {videoElement}
                    {captured && (
                        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
                            <div className="bg-white/80 rounded-lg px-6 py-2 text-lg font-bold text-green-700 border-2 border-green-500 shadow-lg animate-pulse">Image Captured!</div>
                        </div>
                    )}
                    <canvas ref={canvasRef} width={320} height={240} style={{ display: "none" }} />
                    {error && <div className="text-xs text-red-600 text-center">{error}</div>}
                    {controls}
                </div>
            )}

            {isFullscreen && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 pt-8 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-lg p-4 max-w-3xl w-full flex flex-col items-center gap-3">
                        <div className="flex items-center justify-between w-full">
                            <h4 className="text-sm font-semibold text-gray-700">Camera Capture</h4>
                            <button type="button" onClick={() => setIsFullscreen(false)} className="px-3 py-1 rounded bg-gray-200 text-gray-700">Close</button>
                        </div>
                        <div className="w-full flex items-center justify-center relative">
                            <video
                                ref={videoRef}
                                className="rounded border w-full max-w-[900px] h-auto"
                                autoPlay
                                muted
                                onCanPlay={() => setIsReady(true)}
                            />
                            {captured && (
                                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
                                    <div className="bg-white/80 rounded-lg px-6 py-2 text-lg font-bold text-green-700 border-2 border-green-500 shadow-lg animate-pulse">Image Captured!</div>
                                </div>
                            )}
                        </div>
                        <canvas ref={canvasRef} width={320} height={240} style={{ display: "none" }} />
                        {error && <div className="text-xs text-red-600 text-center">{error}</div>}
                        <div className="flex flex-wrap gap-2 items-center justify-center">
                            <select
                                value={cameraFacingMode}
                                onChange={e => setCameraFacingMode(e.target.value)}
                                className="px-2 py-1 rounded border"
                            >
                                <option value="user">Front Camera</option>
                                <option value="environment">Back Camera</option>
                            </select>
                            <button type="button" onClick={() => startCamera(cameraFacingMode)} className="px-3 py-1 rounded bg-blue-600 text-white">Start Camera</button>
                            <button type="button" onClick={capturePhoto} className="px-3 py-1 rounded bg-green-600 text-white" disabled={!isReady}>Capture Photo</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CameraCapture;
